import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { ReactComponent as Check } from "../assets/icons/check.svg";
import { ReactComponent as Cross } from "../assets/icons/x.svg";

export function ShowToast({
  message,
  type,
}: {
  message: ReactNode;
  type: "success" | "error" | "warning";
}) {
  const bg = type === "success" ? "bg-green-50" : type === "warning" ? "bg-yellow-50" : "bg-red-50";
  const border = type === "success" ? "border-green-600" : type === "warning" ? "border-yellow-500" : "border-red-600";
  const iconStroke = type === "success" ? "[&>path]:stroke-green-600" : type === "warning" ? "[&>path]:stroke-yellow-500" : "[&>path]:stroke-red-600";

  toast.custom((_t) => (
    <div
      className={`md:max-w-[50vw] px-4 py-2.5 ${bg} rounded border ${border} border-opacity-20 justify-start items-center gap-1.5 inline-flex`}
    >
      {type === "success" ? (
        <Check className={`w-5 h-5 relative ${iconStroke}`} />
      ) : (
        <Cross className={`w-5 h-5 relative ${iconStroke}`} />
      )}
      <div className="text-zinc-900 text-sm font-medium leading-normal">
        {message}
      </div>
    </div>
  ));
}

export const GENERIC_ERROR_LEAD = "Something went wrong. Please try again.";

/**
 * Fallback for failures that aren't intentionally handled. `lead` replaces the default
 * sentence when the failure has a specific consequence worth naming. The support link opens
 * in a new tab so it can't pull the user out of an active call. Pass `supportLink: false`
 * where there's no session (e.g. mid-login): /support is an authenticated route, so the
 * link would only land on the login page.
 */
export function ShowGenericErrorToast({
  lead = GENERIC_ERROR_LEAD,
  supportLink = true,
}: { lead?: string; supportLink?: boolean } = {}) {
  if (!supportLink) {
    ShowToast({ type: "error", message: lead });
    return;
  }
  ShowToast({
    type: "error",
    message: (
      <>
        {lead} If the issue persists,{" "}
        <a
          href="/support"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          contact support
        </a>
        .
      </>
    ),
  });
}
